import { useEffect, useMemo, useState } from 'react';
import { LocalStore } from 'shared/helpers/Store';
import { createSimilarSearch } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { PostingDTO } from '../../data/dto/PostingDTO';
import { useSearchPostingsList } from '../../data/LoadboardPostingAPI';
import { usePostingsPageContext } from '../postings/PostingsPageContext';

/*
  Similar loads are showed as chunks. This hook returns chunk data without making extra requests.
*/
export function useSimilarLoads(existingPostings: PostingDTO[]) {
  const { params } = usePostingsPageContext();
  const criteria = createSimilarSearch(params.search_criteria);
  const [page, setPage] = useState(1);

  const sort =
    criteria.pickup_venues.length > 0
      ? ['pickup_distance,asc', 'posted_to_loadboard_at,desc']
      : criteria.delivery_venues.length > 0
        ? ['delivery_distance,asc', 'posted_to_loadboard_at,desc']
        : ['posted_to_loadboard_at,desc'];

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchPostingsList({
      page_sort: sort,
      current_page_size: 50,
      search_criteria: criteria,
    });

  const uniqueLoads = useMemo(() => {
    const existingGuids = new Set(
      existingPostings.map((posting) => posting.load.guid),
    );
    return data?.pages
      .flatMap((item) => item.data)
      .filter((posting) => !existingGuids.has(posting.load.guid));
  }, [data, existingPostings]);

  const similarLoads = useMemo(
    () => uniqueLoads?.slice(0, page * 5),
    [uniqueLoads, page],
  );

  function handleShowMore() {
    if (!uniqueLoads) return;

    const nextPage = page + 1;
    const maxPage = Math.ceil(uniqueLoads.length / 5);

    if (nextPage > maxPage && hasNextPage) {
      void fetchNextPage();
    }

    setPage(nextPage);
  }

  function canShowMore() {
    if (!uniqueLoads) return false;

    const nextPage = page + 1;
    const maxPage = Math.ceil(uniqueLoads.length / 5);
    return nextPage <= maxPage || hasNextPage;
  }

  useEffect(() => {
    setPage(1);
  }, [params.search_criteria]);

  return {
    data: similarLoads,
    isFetchingNextPage,
    hasNextPage: canShowMore(),
    fetchNextPage: handleShowMore,
  };
}

/*
  It forces the similar loads section to be hidden if there are less than 10 existing postings.
*/
const SIMILAR_LOADS_HIDDEN_KEY = 'slb_similar_loads_hidden';
export function useSimilarLoadsVisibility(existingPostings: PostingDTO[]) {
  const [isHidden, setHidden] = useState(
    () => !!LocalStore.get(SIMILAR_LOADS_HIDDEN_KEY),
  );

  useEffect(() => {
    if (existingPostings.length < 10) {
      setHidden(false);
    }
  }, [existingPostings.length]);

  useEffect(() => {
    if (isHidden) {
      LocalStore.set(SIMILAR_LOADS_HIDDEN_KEY, 'true');
    } else {
      LocalStore.delete(SIMILAR_LOADS_HIDDEN_KEY);
    }
  }, [isHidden]);

  return [isHidden, setHidden] as const;
}
